import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import api from '../../utils/api';
import useLiveChannel from '../../hooks/useLiveChannel';
import { mergeMessages } from '../../utils/chat/messages';
import { useFieldGeo } from '../field/FieldGeoProvider';

/*
 * A live field event's heartbeat (routes/event_live.py; Part III rules 1-3).
 *
 * While the person is out on an event (the active mission says so), this asks
 * POST /events/{id}/live every few seconds -- the interval the server names,
 * nothing while the page is hidden, backing off on failures (utils/live/) --
 * sending the latest position and keeping what comes back: the phase, my next
 * waypoint, teammates' positions, the event's chat after the last message
 * seen, and the scoreboard whenever its version moved.
 *
 * A position goes up only after the person agreed to share it (the server
 * keeps none otherwise), and only in the body, never the address. An arrival
 * the server decided comes back once, as `arrived`, for the briefing.
 */
const EventLiveContext = createContext(null);

export function useEventLive() {
  return useContext(EventLiveContext);
}

function positionOf(fix) {
  if (!fix || fix.lat == null || fix.lng == null) return null;
  return { lat: fix.lat, lng: fix.lng, acc_m: fix.accuracy_m ?? null,
    fix_at: fix.at ? new Date(fix.at).toISOString() : undefined };
}

export function EventLiveProvider({ missionId, children, onArrive }) {
  const geo = useFieldGeo();
  const [state, setState] = useState({ live: null, messages: [], board: null, error: null });
  const cursors = useRef({ chat: 0, score: null });
  const fixRef = geo.fixRef;
  const arriveRef = useRef(onArrive);
  arriveRef.current = onArrive;
  const [arrival, setArrival] = useState(null);
  // The last arrival stays after its briefing closes: where the radius scan works.
  const [lastArrival, setLastArrival] = useState(null);

  useEffect(() => {
    cursors.current = { chat: 0, score: null };
    setState({ live: null, messages: [], board: null, error: null });
    setArrival(null);
    setLastArrival(null);
  }, [missionId]);

  const pull = useCallback(async () => {
    const consent = state.live?.me?.location_consent;
    const body = { pos: consent ? positionOf(fixRef.current) : null,
      after: { chat: cursors.current.chat, score: cursors.current.score } };
    try {
      return (await api.post(`/events/${missionId}/live`, body)).data;
    } catch (e) {
      // Too soon (another tab, a quick poke): nothing changed, ask again later.
      if (e?.response?.status === 429) return null;
      throw e;
    }
  }, [missionId, fixRef, state.live?.me?.location_consent]);

  const onData = useCallback((data) => {
    if (!data) return;
    cursors.current.chat = Math.max(cursors.current.chat, data.chat_latest || 0);
    if (data.scoreboard) cursors.current.score = data.score_version;
    setState((s) => ({
      live: data,
      messages: data.chat?.length ? mergeMessages(s.messages, data.chat) : s.messages,
      board: data.scoreboard || s.board,
      error: null,
    }));
    if (data.me?.arrived) {
      setArrival(data.me.arrived);
      setLastArrival(data.me.arrived);
      arriveRef.current?.(data.me.arrived);
    }
  }, []);

  const onError = useCallback((e) => setState((s) => ({ ...s, error: e })), []);
  const ended = state.live && state.live.next_in_ms == null;
  const poke = useLiveChannel(pull, onData, {
    enabled: !!missionId && !ended, base: state.live?.next_in_ms || 5000, key: missionId, onError,
  });

  const value = { missionId, ...state, fix: geo.fix, gpsState: geo.state, arrival, lastArrival,
    clearArrival: () => setArrival(null), poke };
  return <EventLiveContext.Provider value={value}>{children}</EventLiveContext.Provider>;
}
